import { agreementAllowsCloudStorage } from '../domain/agreement';
import { AGREEMENT_NEW_BUILD_REPORT_TITLE_EN } from '../domain/agreementText';
import { isInspectionLocked } from '../domain/inspection';
import { inspectionServiceOf } from '../domain/inspectionService';
import { addendumFileName, createReportRecord, type V2ReportRecord } from '../domain/report';
import { signatureIsMeaningful, signatureSvg, type SignatureStrokes } from '../domain/signature';
import { licenseHolderName } from '../../supabase/functions/_shared/accountProtocol';
import { translate } from '../i18n/i18n';
import { resizeImage } from '../native/imageTools';
import { accountGuard } from '../persistence/accountScope';
import { putReportWithMedia } from '../persistence/v2Database';
import { renderAddendumHtml, type AddendumModel } from '../report/addendumHtml';
import { REPORT_PHOTO_MAX_EDGE } from '../report/assembly';
import type { PdfRenderer } from '../report/pdf';
import { boundLicenseState, cachedBoundLicense } from './boundLicense';
import { inspectionStore } from './inspectionStore';
import { currentInspectorProfile } from './preferences';
import { brandingForPrint } from './reportBranding';
import { flushReportUploads, queueReportUploads } from './reportCloudStore';
import { reportStore } from './reportStore';

// 「补充说明 / Addendum」 (Owner decision list 2026-10-19 item 27). OD-12 stays exactly as it is — the
// delivered report is never rewritten and the working data stays deleted. When the inspector finds an error
// after delivery, they issue an addendum: dated, signed, naming the item it corrects and why, optionally
// with photos from their own library. It is stored like a report PDF (record + bytes on the device), copied
// to the account with the report (item 25) and shared to the client from the same list.
export const ADDENDUM_MAX_PHOTOS = 4;

export interface AddendumInput {
  item: string;
  reason: string;
  correction: string;
  photos: readonly Blob[];
  signature: SignatureStrokes | null;
}

export type AddendumField = 'item' | 'reason' | 'correction' | 'signature';

export type AddendumOutcome =
  | { ok: true; record: V2ReportRecord; number: number }
  | { ok: false; reason: 'notFound' | 'notDelivered' | 'licenceMissing' | 'licenceExpired' | 'renderFailed'; detail: string }
  | { ok: false; reason: 'invalid'; fields: AddendumField[]; detail: string };

export function missingAddendumFields(input: Pick<AddendumInput, 'item' | 'reason' | 'correction' | 'signature'>): AddendumField[] {
  const missing: AddendumField[] = [];
  if (input.item.trim().length === 0) missing.push('item');
  if (input.reason.trim().length === 0) missing.push('reason');
  if (input.correction.trim().length === 0) missing.push('correction');
  if (!signatureIsMeaningful(input.signature)) missing.push('signature');
  return missing;
}

// The next number of this inspection's addenda (1, 2, …), counted from the addenda on this device.
export function nextAddendumNumber(inspectionId: string, reports: readonly V2ReportRecord[] = reportStore.get().reports): number {
  return reports.filter((r) => r.inspectionId === inspectionId && r.variant === 'addendum').length + 1;
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });
}

async function photoDataUrl(blob: Blob): Promise<string | null> {
  try {
    const resized = await resizeImage(blob, { maxEdge: REPORT_PHOTO_MAX_EDGE, quality: 0.8 });
    return blobToDataUrl(resized ? resized.blob : blob);
  } catch {
    return blobToDataUrl(blob);
  }
}

export async function createAddendumAction(inspectionId: string, input: AddendumInput, deps: { renderer: PdfRenderer; now?: number }): Promise<AddendumOutcome> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!inspection) return { ok: false, reason: 'notFound', detail: '' };
  // An addendum belongs to a DELIVERED report: before that, the inspector simply edits the inspection.
  if (!isInspectionLocked(inspection)) return { ok: false, reason: 'notDelivered', detail: '' };
  const missing = missingAddendumFields(input);
  if (missing.length > 0) return { ok: false, reason: 'invalid', fields: missing, detail: missing.join(',') };
  const now = deps.now ?? Date.now();
  const demo = inspection.agreement.demo;
  // A signed document to the client: like the report, it needs the account's bound, current licence.
  const license = cachedBoundLicense();
  if (!demo) {
    const state = boundLicenseState(now);
    if (state === 'unbound' || !license) return { ok: false, reason: 'licenceMissing', detail: '' };
    if (state === 'expired') return { ok: false, reason: 'licenceExpired', detail: '' };
  }
  const sameAccount = accountGuard();
  const service = inspectionServiceOf(inspection.property);
  const number = nextAddendumNumber(inspectionId);
  const branding = await brandingForPrint();
  const profile = currentInspectorProfile();
  const photos: string[] = [];
  for (const blob of input.photos.slice(0, ADDENDUM_MAX_PHOTOS)) {
    const url = await photoDataUrl(blob);
    if (url) photos.push(url);
  }
  const p = inspection.property;
  const signature = input.signature as SignatureStrokes;
  const model: AddendumModel = {
    number,
    demo,
    issuedAt: now,
    reportTitle: service === 'newBuild' ? AGREEMENT_NEW_BUILD_REPORT_TITLE_EN : translate('en-US', 'report.title'),
    originalIssuedAt: inspection.finalizedAt ?? inspection.purge?.generatedAt ?? null,
    address: [p.address, p.city, [p.state, p.zip].filter((s) => s.trim().length > 0).join(' ')].map((s) => s.trim()).filter((s) => s.length > 0).join(', '),
    clientName: inspection.client.name.trim(),
    item: input.item.trim(),
    reason: input.reason.trim(),
    correction: input.correction.trim(),
    photos,
    inspectorName: license ? licenseHolderName(license) : profile.name,
    licenseNumber: license ? license.licenseNumber : '',
    signatureSvg: signatureSvg(signature, { width: 220, height: Math.round((220 * signature.height) / Math.max(1, signature.width)) }),
    companyName: branding.companyName,
    contact: branding.contact,
    logo: branding.logo
  };
  try {
    const pdf = await deps.renderer.render(renderAddendumHtml(model));
    if (!sameAccount()) return { ok: false, reason: 'renderFailed', detail: 'the account was left while the addendum was being created' };
    const record = createReportRecord(
      { inspectionId, language: 'en-US', variant: 'addendum', fileName: addendumFileName(p.address, now, number, demo, service), byteSize: pdf.bytes.byteLength, pageCount: pdf.pageCount },
      now
    );
    await putReportWithMedia(record, { type: 'application/pdf', bytes: pdf.bytes });
    reportStore.set((prev) => ({ ...prev, reports: [...prev.reports, record] }));
    // Kept in the account with the report only when the client consented to that storage (v4 consent clause (2)).
    if (agreementAllowsCloudStorage(inspection.agreement)) queueReportUploads([record], [p.address, p.city].filter((part) => part.trim().length > 0).join(', '));
    void flushReportUploads();
    return { ok: true, record, number };
  } catch (raw) {
    return { ok: false, reason: 'renderFailed', detail: raw instanceof Error ? raw.message : String(raw) };
  }
}
